import React, { useEffect, useMemo, useState } from 'react';

// DEPENDENCIES
import isUndefined from 'lodash/isUndefined';
import orderBy from 'lodash/orderBy';
import sumBy from 'lodash/sumBy';
import toString from 'lodash/toString';
import { compose } from 'redux';
import { useFormikContext } from 'formik';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { NEW_USED } from 'codes';

// GLOBAL FUNCTIONS
import { apiFetch, formatDollar, makePath } from 'functions.js';

// GLOBAL HELPERS
import { withRouter } from 'helpers/withRouter';
import useRecordView from 'helpers/getRecordView';

// GLOBAL COMPONENTS
import Card from 'parts/card/Card';
import Form from 'core/form/Form';

// LOCAL COMPONENTS
import InputAppendCol from './parts/InputAppendCol';
import RepairInvoice from './parts/RepairInvoice';
import DmsTooltip from '../routes/sales/tools/DmsTooltip';

// STYLE
import styles from './inventoryInternal.module.scss';

// MAIN COMPONENT
const InventoryInternalForm = compose(withRouter)(({ location: { search } }) => {
  const [pack, setPack] = useState([]);

  const { isAdd, isView } = useRecordView();
  const { values } = useFormikContext() || {};

  const {
    aryInventoryDealerPack = [],
    aryRepairInvoice = [],
    invoice_cost,
    invoice_cost_with_inventory_pack,
    invoice_cost_with_repair_invoice,
    invoice_cost_with_inventory_pack_dms_invoice_difference,
    dmsinvoice,
    dms_invoice_price,
    new_used,
    inventory_source_id
  } = values || {};

  const params = new URLSearchParams(search);
  const newUsedParam = params.get('new_used');
  const newUsed = newUsedParam ? +newUsedParam : new_used;
  const isNew = newUsed === NEW_USED.new.numeric;

  const type = !isUndefined(newUsed) ? (isNew ? 'Factory Invoice' : 'ACV') : '';

  const inventoryPackTotal = useMemo(() => {
    return !isAdd ? sumBy(aryInventoryDealerPack, 'item_amount') : sumBy(pack, 'pack_amount');
  }, [aryInventoryDealerPack, pack, isAdd]);

  const invoiceCostWithInventoryPack = useMemo(() => {
    const total = isAdd && !invoice_cost ? '' : +inventoryPackTotal + +(invoice_cost || 0);
    return total;
  }, [inventoryPackTotal, invoice_cost, isAdd]);

  const repairInvoiceTotal = useMemo(() => {
    return sumBy(aryRepairInvoice, 'invoice_amount');
  }, [aryRepairInvoice]);

  const invoiceCostWithRepairInvoice = useMemo(() => {
    return +repairInvoiceTotal + invoiceCostWithInventoryPack;
  }, [repairInvoiceTotal, invoiceCostWithInventoryPack]);

  const dmsCost = `${dms_invoice_price ? formatDollar(dms_invoice_price) : 'Not available'} ${dmsinvoice}`;

  useEffect(() => {
    if (isAdd && !!toString(inventory_source_id)) {
      apiFetch({
        endpoint: makePath(ENDPOINTS.inventory.package, inventory_source_id),
        params: {
          newUsed: newUsed
        },
        onSuccess: data => setPack(data?.result || []),
        // loadingMessage: 'Loading Inventory Packages',
        errorMessage: 'Unable to get Inventory Packages.'
      });
    }
  }, [inventory_source_id, isAdd, newUsed]);

  return (
    <Form.Body plaintext>
      <Form.PlainText
        label={`${type} ${isNew ? 'Cost' : ''}`}
        value={invoice_cost}
        schema="dollars"
        input={{
          append:
            pack?.length || aryInventoryDealerPack?.length   ? (
              <>
                <InputAppendCol cols={{ sm: 8 }} />
                <InputAppendCol cols={{ sm: 8 }} className='fw-bolder'>
                  Account to Credit
                </InputAppendCol>
              </>
            ) : undefined
        }}
      />
      {!isAdd ? orderBy(aryInventoryDealerPack, ['item_name'], ['asc']).map((v, i) => (
        <Form.PlainText
          key={v.inventory_dealer_pack_id}
          label={`Inventory Pack ${i + 1}`}
          value={v.item_amount}
          schema="dollars"
          input={{
            append: (<>
              <InputAppendCol cols={{ sm: 8 }}>{v.item_name}</InputAppendCol>
              <InputAppendCol cols={{ sm: 8 }}>{v.item_account_to_credit}</InputAppendCol>
            </>)
          }}
        />
      )) : pack.map((v, i) => (
        <Form.PlainText
          key={v.inventory_pack_id}
          label={`Inventory Pack ${i + 1}`}
          value={v.pack_amount}
          schema="dollars"
          input={{
            append: (<>
              <InputAppendCol cols={{ sm: 8 }}>{v.pack_title}</InputAppendCol>
              <InputAppendCol cols={{ sm: 8 }}>{v.pack_account_to_credit}</InputAppendCol>
            </>)
          }}
        />
      ))}
      {(inventoryPackTotal || invoice_cost) &&
        <Form.PlainText
          label="Inventory Pack Total"
          value={inventoryPackTotal}
          schema="dollars"
        />
      }
      <Form.PlainText
        label={`${type} with Inventory Pack Total`}
        value={invoiceCostWithInventoryPack || invoice_cost_with_inventory_pack || invoice_cost}
        schema="dollars"
      />
      {!isAdd && <>
        <RepairInvoice />
        <Form.PlainText
          label={`${type} with Repair Invoices`}
          value={invoiceCostWithRepairInvoice || invoice_cost_with_repair_invoice || 0}
          schema="dollars"
        />
      </>}
      {isView && 
        <Form.Control
          label="DMS Cost"
          fullWidth
          suffix={<DmsTooltip />}
          className={styles.span}
          input={{ title: dmsCost }}
          value={dmsCost}
        />
      }
      {dms_invoice_price &&
        <Form.PlainText
          label={`${type} with Inventory Pack Total vs. DMS Cost Difference`}
          value={
            invoice_cost_with_inventory_pack_dms_invoice_difference
              ? `${formatDollar(Math.abs(invoice_cost_with_inventory_pack_dms_invoice_difference))}`
              : '$ 0.00' // TODO: Look into removing formatDollar and using schema="dollars"
          }
        />
      }
    </Form.Body>
  );
});

// EXPORT
export default function InventoryInternal(props) {
  return <Card title="Inventory - Internal" body={<InventoryInternalForm {...props} />} />;
}
